/**
 * Compliance-related translations: geofencing, age verification, advisory.
 */

export const compliance = {
  geofence: {
    title: 'Service Unavailable in Your Region',
    description: 'Due to local legislation, this service is not available in your jurisdiction.',
    jurisdictionLabel: 'Jurisdiction',
    viewLaw: 'View relevant legislation',
    close: 'Close',
  },
  ageVerification: {
    title: 'Age Verification Required',
    description: 'Your jurisdiction requires age verification before you can create or access aliases.',
    startButton: 'Start Verification',
    resumeButton: 'Resume Verification',
    retryButton: 'Retry Verification',
    close: 'Close',
    approved: 'Age verification complete. You may now proceed.',
    loginToAlias: 'Login to Alias',
    createAlias: 'Create Alias',
    failedMessage: 'Sorry, age verification failed and due to your local legislation we are unable to grant access. You may retry after the cooldown period.',
    expiredMessage: 'Your verification session expired before completion.',
    cooldownMessage: 'Your verification session expired. You may retry after the cooldown period.',
    retryAfterLabel: 'Retry available after',
    expirationCount: 'Expiration {{count}} of {{max}}',
    starting: 'Starting verification...',
    awaitingUser: 'Complete the verification in the opened window.',
    processing: 'Verification in progress...',
    inProgressBody:
      'Finish verification in the AgeMin window that opened, then come back to this app. We check the result automatically, and you can check sooner.',
    checkNow: 'Refresh Status',
    reopenWindowHint: 'Closed the AgeMin window by accident?',
    reopenWindowLink: 'Reopen and continue',
    referenceLabel: 'Reference',
    appIdLabel: 'App ID',
    nextCheckIn: 'Checking status in {{seconds}}s...',
    doLater: 'Do verification later',
    doLaterConfirmTitle: 'Stop verification for now?',
    doLaterConfirmBody:
      "Are you sure? Unless your location changes, we'll be required by law to block your access until we can determine you're over 18.",
    doLaterConfirm: 'Do verification later',
    doLaterStay: 'Keep verifying',
    methodsTitle: 'Verification Methods',
    attemptsRemaining: '{{remaining}} attempts remaining',
    abusiveIpReason:
      'Your account was accessed from a network with a known abusive history. Age verification is required before you can create or access aliases.',
    emailConsentDescription:
      'We can attempt to verify your age using your email address. No additional information is required.',
    emailConsentAgree: 'I Agree',
    emailConsentDecline: 'Later',
    emailConsentPageTitle: 'Email-Based Age Verification Available',
    emailConsentEmailLabel: 'Email address:',
    emailConsentInfoShareIntro: 'Your jurisdiction requires we verify your age.',
    emailConsentInfoShareIntroBody: "Like a bouncer at a bar, we only care that you're above a certain age: we don't care who you are, only that you're allowed to be here. Your jurisdiction (based on your IP) requires that we check your age: in most cases, we can do this with only an email.",
    emailConsentInfoShareTitle: 'How it works',
    emailConsentInfoShareBody:
      "Adieuu will share your email address with our partner, {{provider}}. {{provider}} will share your email address with its third-party partners to review sites, apps, and other data sources where you've previously used this email address, in accordance with their privacy policy, to determine if you're over 18.",
    emailConsentInfoAgeBody:
      "Adieuu will use the info from {{provider}} to enable your access to the platform. We can not see any of your identity information (no name, no DOB, not even age). All they tell us is that the check completed, and whether or not you are over 18 (true/false): basic info like that is all we store. This information is never attached to any Aliases, so your messages and activity aren't linked to your email or other information.",
    emailConsentInfoInstantTitle: "Your real info is never shared with us.",
    emailConsentInfoInstantBody:
      "We can't see your real info, and it can't be tied to your Aliases. We see that you passed the check and your jurisdiction. This is tied only to your Account: your Aliases (what you post or message as) are cryptographically separated and can't be linked back to your Account in our DB. This way we can comply with the law, and your activity stays private & anonymous.",
    emailConsentLearnVerifyMy: 'Learn how {{provider}} estimates age',
    emailConsentLearnAgeVerification: 'Learn more about age verification',
    phoneOnlyIntro:
      'Your jurisdiction requires age verification. Since your account uses a phone number, you will be directed to our verification partner to complete the process.',
    goToDashboard: 'Go to Dashboard',
    emailCheckInconclusive:
      "Sorry, we were not able to verify your age via email alone. There may be other verification methods available through {{provider}}. Any information they obtain as part of the verification is immediately discarded after verification. We geolocate based on IP, so as long as your jurisdiction requires you to verify age, we'll be unable to offer further access until you verify.",
    checkingEmail: 'Checking your email address (this may take a few moments)\u2026',
    continueOtherMethods: 'Continue with Other Methods',
    emailOnlyInconclusive:
      "We could not confirm your age from your email address, and email is the only method your jurisdiction allows us to use. Nothing was shared beyond your email address, and there is no other check we can offer you here. You can try again later — the result can change as the address builds more history — or contact support if you believe this is wrong.",
    emailOnlyInconclusiveRetry: 'Try Again',
  },
  vpn: {
    title: "Looks like you're on a VPN - awesome!",
    body: "It looks like you're connecting through an anonymizing service — we admire that you're taking steps to protect your privacy!",
    vpnHint: "We have no issues with VPN use (we applaud it!), but we do need to make you aware that we presently do <strong>NOT</strong> provide service to the countries below. Similarly, we can not provide service to you if you connect your VPN through a node detected in a listed country.",
    sanctionedIntro: "Connection from a country below, even if you are not a resident or national, results in a permanent ban and cannot be appealed.",
    sanctionedQuestion: 'Are you a resident or national of any of the above countries?',
    utahQuestion: 'Do you live in Utah?',
    utahNotice:
      "Due to Utah's ridiculous regulations, we have to verify your age. Sorry.",
    utahContinue: 'Continue',
    sessionRefreshFailed:
      'Your response was saved, but we could not refresh your session. Please try again.',
    yes: 'Yes, I am',
    no: "No, I'm not",
  },
  abusiveIp: {
    title: 'This network is not welcome here',
    body: 'The IP address you are connecting from has been flagged for abusive behaviour elsewhere on the internet.',
    vpnHint:
      'If you are on a VPN, this might be a shared IP with a colourful past. Consider upgrading to a provider that does not recycle IPs from the bargain bin.',
    logoutNotice:
      'We have ended your session. Please sign in again from a different network.',
    acknowledge: 'Got it',
  },
  subscription: {
    title: 'Subscription Required',
    description: 'An active subscription (Access or Insider) is required to create or log into an Alias.',
    expiredDescription: 'Your subscription has expired. Please renew your plan to continue using Aliases.',
    subscribeCta: 'View Plans',
  },
  advisory: {
    unresolvedJurisdiction: 'We could not determine your jurisdiction. You are responsible for adhering to local age verification laws. You may opt in to verification voluntarily.',
    optInButton: 'Verify my age',
    optInDescription: 'You are choosing to verify your age voluntarily. Please enter the 2-letter ISO country code for the jurisdiction whose rules should apply.',
    countryLabel: 'Country code (e.g. US, DE, GB)',
  },
  jurisdictionRequirement: {
    proposed: 'Proposed / Not in Force',
    legislation: 'Relevant Laws or Instruments',
    requirements: 'Requirements We Track',
    methods: 'Compliant Verification Methods:',
    notes: 'Notes',
    regulatoryBody: 'Regulator',
    empty: "We aren't aware of any regulations that require age verification for this jurisdiction. You can use Adieuu where our terms and your local law allow.",
    emptyFree:
      "We aren't aware of any regulations that require age verification for this jurisdiction. However, because your account is free, age verification is still required to reduce botting. A paid plan would bypass this requirement entirely.",
    catalogEmpty: 'No jurisdictions requiring verification are listed at this time.',
    loadError: 'Unable to load jurisdiction requirements. Please try again later.',
    filterPlaceholder: 'Filter by name or code',
    filterLabel: 'Filter jurisdictions',
    noFilterResults: 'No jurisdictions match your filter.',
  },
  admin: {
    title: 'Age/Geofencing',
    subtitle: 'Configure age verification enforcement, geofencing, provider settings, and NCMEC CyberTipline.',
    enabledLabel: 'Enforcement Enabled',
    enabledDescription: 'When enabled, users in configured jurisdictions must verify their age before accessing aliases.',
    autoEmailBgLabel: 'Automatic email background check',
    autoEmailBgDescription:
      'After a subscribed user completes checkout, silently run a provider background check using their verified email.',
    providerLabel: 'Active Provider',
    providerDescription:
      'Applies immediately, with no restart. Verifications already in progress keep polling the provider that started them.',
    providerNotConfigured:
      '{{provider}} credentials are not configured on this server. Verifications will fail until the required environment variables are set.',
    environmentLabel: 'VerifyMy Environment',
    defaultBusinessSettingsIdLabel: 'Default VerifyMy Business Settings ID',
    defaultBusinessSettingsIdPlaceholder: 'e.g. bs-us-default',
    defaultBusinessSettingsIdDescription:
      'Used when a jurisdiction has no per-jurisdiction business settings ID configured. Leave empty to omit the field from VerifyMy requests.',
    ageminEnvironmentLabel: 'Agemin Environment',
    ageminDefaultAssetIdLabel: 'Default Agemin Asset ID',
    ageminDefaultAssetIdPlaceholder: 'e.g. ast_YOUR_ASSET_ID',
    ageminDefaultAssetIdDescription:
      'Hosted fallback asset, used when a jurisdiction has no per-jurisdiction asset ID configured. The asset carries the age threshold and enabled methods, configured in the Agemin dashboard. Leave email disabled on this asset: the email check runs on the email-only asset below.',
    ageminEmailAssetIdLabel: 'Agemin Email-Only Asset ID',
    ageminEmailAssetIdPlaceholder: 'e.g. ast_YOUR_EMAIL_ASSET_ID',
    ageminEmailAssetIdDescription:
      'Shared asset with only the email check enabled, used for the silent background check against the address on the account. Keeping it separate from the hosted assets means a user who fails cannot retry in the Agemin UI with a different email. Leave empty to skip the silent email check entirely.',
    ncmecEnvironmentLabel: 'NCMEC CyberTipline Environment',
    ncmecEnvironmentTest: 'Testing (exttest)',
    ncmecEnvironmentProduction: 'Production',
    ncmecEnvironmentDescription:
      'Controls which NCMEC CyberTipline endpoint moderator LE reports use (exttest vs production). Uses the same CYBERTIPLINE_* credentials for both.',
    modeLabel: 'Required Mode',
    modeJurisdictions: 'Jurisdictions (seed-data-driven)',
    modeAll: 'All accounts',
    jurisdictionsLabel: 'Additional Jurisdictions to Verify',
    jurisdictionsPlaceholder: 'e.g. US-TN, IT',
    effectiveJurisdictionsTitle: 'Required Jurisdictions ({{count}})',
    effectiveJurisdictionsAllModeTitle: 'All Accounts',
    effectiveJurisdictionsAllModeDescription:
      'Age verification is required for every account regardless of jurisdiction.',
    effectiveJurisdictionsEmpty: 'No jurisdictions currently require age verification.',
    effectiveJurisdictionsSourceAdmin: 'Admin override',
    effectiveJurisdictionsLoadError: 'Could not load jurisdiction catalog.',
    blockedLabel: 'Geofence-Blocked Jurisdictions',
    blockedPlaceholder: 'e.g. US-TX',
    lawLinksLabel: 'Law Links (jurisdiction|url)',
    lawLinksPlaceholder: 'e.g. US-TN|https://...',
    saveButton: 'Save Settings',
    saveSuccess: 'Age verification settings saved.',
    saveError: 'Failed to save settings.',
    loadError: 'Failed to load settings.',
    sanctionedCountriesTitle: 'OFAC / Sanctioned Countries',
    sanctionedCountriesDescription:
      'Countries subject to export-control enforcement. Active rows block access and appear in VPN attestation. Separate from geofence-blocked jurisdictions.',
    sanctionedCountriesPermabanWarning:
      'Warning: accounts that establish a session from an active OFAC/sanctioned location are banned while the country remains sanctioned. Self-attesting sanctioned residency on VPN also results in a ban.',
    sanctionedCountriesCount: '{{active}} active / {{total}} total',
    sanctionedCountriesEmpty: 'No sanctioned countries in the database. Run the seed or add a country below.',
    sanctionedCountriesLoadError: 'Could not load sanctioned countries.',
    sanctionedCountriesCodeHeader: 'Code',
    sanctionedCountriesNameHeader: 'Country',
    sanctionedCountriesProgramHeader: 'Program',
    sanctionedCountriesActiveHeader: 'Active',
    sanctionedCountriesActiveYes: 'Yes',
    sanctionedCountriesActiveNo: 'No',
    sanctionedCountriesEdit: 'Edit',
    sanctionedCountriesAddTitle: 'Add country',
    sanctionedCountriesEditTitle: 'Edit {{code}}',
    sanctionedCountriesCodeLabel: 'ISO country code',
    sanctionedCountriesNameLabel: 'Country name',
    sanctionedCountriesProgramLabel: 'Program',
    sanctionedCountriesActiveLabel: 'Active for enforcement',
    sanctionedCountriesAddButton: 'Add country',
    sanctionedCountriesUpdateButton: 'Save changes',
    sanctionedCountriesCancelEdit: 'Cancel',
    sanctionedCountriesInvalidCode: 'Country code must be a 2-letter ISO code (e.g. CU).',
    sanctionedCountriesSaveSuccess: 'Sanctioned country saved.',
    sanctionedCountriesSaveError: 'Failed to save sanctioned country.',
    sanctionedCountriesSeedTitle: 'Run bundled seed',
    sanctionedCountriesSeedDescription:
      'Upserts the maintainer OFAC seed file into the database. Additive leaves extra DB rows unchanged; clobber deactivates countries not in the seed.',
    sanctionedCountriesSeedAdditive: 'Additive (upsert seed rows only)',
    sanctionedCountriesSeedClobber: 'Clobber (upsert seed, deactivate others)',
    sanctionedCountriesSeedButton: 'Run seed',
    sanctionedCountriesSeedClobberConfirm:
      'Deactivate all sanctioned countries that are not in the bundled seed file?',
    sanctionedCountriesSeedSuccess: 'Seed complete: {{upserted}} upserted, {{deactivated}} deactivated.',
    sanctionedCountriesSeedError: 'Failed to run sanctioned countries seed.',
    jurisdictionRequirementsTitle: 'Jurisdiction Requirements',
    jurisdictionRequirementsDescription:
      'Regulatory jurisdictions requiring age verification. Optionally configure a per-jurisdiction profile for each provider; otherwise the platform default above is used. Both providers can be configured at once, so a jurisdiction is ready before you switch.',
    jurisdictionRequirementsCount: '{{configured}} with a profile for the active provider / {{total}} total',
    jurisdictionRequirementsEmpty:
      'No jurisdiction rows in the database. Run the seed or add regulatory data via the maintainer seed file.',
    jurisdictionRequirementsLoadError: 'Could not load jurisdiction requirements.',
    jurisdictionRequirementsCodeHeader: 'Code',
    jurisdictionRequirementsNameHeader: 'Jurisdiction',
    jurisdictionRequirementsRegionHeader: 'Region',
    jurisdictionRequirementsBusinessIdHeader: 'VerifyMy Business Settings ID',
    jurisdictionRequirementsAgeminAssetIdHeader: 'Agemin Asset ID',
    jurisdictionRequirementsProfileInUse: '(in use)',
    jurisdictionRequirementsEdit: 'Edit',
    jurisdictionRequirementsEditTitle: 'Edit {{code}}',
    jurisdictionRequirementsCodeLabel: 'Jurisdiction code',
    jurisdictionRequirementsNameLabel: 'Name',
    jurisdictionRequirementsRegionLabel: 'Region',
    jurisdictionRequirementsBusinessIdLabel: 'VerifyMy business settings ID',
    jurisdictionRequirementsBusinessIdPlaceholder: 'Optional — leave empty to use platform default',
    jurisdictionRequirementsAgeminAssetIdLabel: 'Agemin asset ID (hosted methods)',
    jurisdictionRequirementsAgeminAssetIdPlaceholder:
      'Optional — leave empty to use platform default',
    jurisdictionRequirementsBusinessIdHint:
      'Clear a field and save to remove that per-jurisdiction override and fall back to the default platform setting. Agemin assets decide their own method list, so configure each one with only the methods this jurisdiction allows, and leave email disabled — the silent email check uses the shared email-only asset.',
    jurisdictionRequirementsUpdateButton: 'Save changes',
    jurisdictionRequirementsCancelEdit: 'Cancel',
    jurisdictionRequirementsSaveSuccess: 'Jurisdiction provider profiles saved.',
    jurisdictionRequirementsSaveError: 'Failed to save jurisdiction provider profiles.',
    jurisdictionRequirementsSeedTitle: 'Run bundled seed',
    jurisdictionRequirementsSeedDescription:
      'Upserts the maintainer jurisdiction seed file into the database. Additive preserves admin-configured provider profiles when the seed row omits them; clobber clears profiles not defined in the seed.',
    jurisdictionRequirementsSeedAdditive: 'Additive (preserve admin provider profiles)',
    jurisdictionRequirementsSeedClobber: 'Clobber (reset provider profiles from seed only)',
    jurisdictionRequirementsSeedButton: 'Run seed',
    jurisdictionRequirementsSeedClobberConfirm:
      'Clear per-jurisdiction business settings IDs that are not defined in the bundled seed file?',
    jurisdictionRequirementsSeedSuccess: 'Seed complete: {{upserted}} jurisdictions upserted.',
    jurisdictionRequirementsSeedError: 'Failed to run jurisdiction requirements seed.',
  },
} as const;
